/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICache } from '@msdyn365-commerce/cache-internal';
import {
    IDataActionOptions,
    IDictionary,
    IRequestContext,
    IResourceManager,
    IThemeModule,
    MSDyn365Commerce
} from '@msdyn365-commerce/core-internal';
import * as React from 'react';

export interface IDataAction {
    moduleName?: string;
    id: string;
    name: string;
    path: string;
    runOn: ActionRuntime;
    options?: IDataActionOptions;
}

export enum ActionRuntime {
    server,
    client,
    serverOnly
}

export interface IModuleDefinition {
    name: string;
}

export interface IModuleBinder {
    $type: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    componentAsync: () => Promise<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: any;
    dataActions: IDataAction[];
    definitionExtensions?: string[];
    definitionPath: string;
    isNodeModule: boolean;
    moduleNamespace: string;
    chunkName: string;
    name: string;
    packageName: string;
    pages?: IDictionary<string[]>;
    parentDefinitionPath: string;
    path: string;
    segments: string[];
    moduleDirectory: string;
    themeSettings: string;
}

export interface IModuleBinderPartial {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    c: () => Promise<any>;
    mn: string;
    md: string;
    da: IDataAction[];
    p: string;
    n: string;
    pdp: string;
    iNM: boolean;
    ns: string;
}

export interface IPrototype {
    prototype: IMSDyn365CommerceExtension;
}

interface IDAPIBindings {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    modules: IDictionary<any>;
    themes: IThemeModule[];
}

export interface IBinding {
    modules: IDictionary<IModuleBinder>;
    dataActions: IDictionary<Function>;
    views: IDictionary<React.FC>;
    components: IDictionary<React.FC>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    connectors: IDictionary<any>;
    dapi: IDAPIBindings;
}

export interface IMSDyn365CommerceExtension {
    bindings: IBinding;
    appCache: IDictionary<ICache>;
    resourceManager: IResourceManager;
    getResourceManager(): IResourceManager;
    dataAction(name: string): Function;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getConnector(name: string): any;
    getAppCache(requestContext: IRequestContext): ICache;
    moduleBinder(name: string): IModuleBinder;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    viewBinder(name: string): any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    componentBinder(name: string): any;
    getAllModuleBinder(): IModuleBinder[];
    updatePrivateVariable(): void;
}

export type IMSDyn365CommerceWithPrototype = typeof MSDyn365Commerce & IPrototype;
